import { CommonModule, formatNumber } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, LOCALE_ID, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogModule as MatDialogModule,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarModule as MatSnackBarModule,
} from '@angular/material/legacy-snack-bar';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GridColDef, GridRenderCellProps, GridValueGetterParams, OrderModelsService } from 'cloud-shared-lib';
import {
  BehaviorSubject,
  combineLatest,
  finalize,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { CadModelsDetailsInfoMaterial } from '../../../models/cutting/cutting-cad-model-details.model';
import { DataGridComponent } from '../../data-grid/data-grid.component';
import { GridDescriptionParams } from '../../data-grid/models/grid-value-description-params.model';
import { CadModelDetailsCellNoteComponent, CommentChangeEvent } from './cadmodel-details-cell-note.component';

export const DataDisplayType = {
  Original: 'original',
  Production: 'production',
} as const;

type DataDisplayTypeValue = typeof DataDisplayType[keyof typeof DataDisplayType];

@Component({
  selector: 'cutting-cad-model-details-dialog',
  templateUrl: './cadmodel-details-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatDialogModule,
    MatIconModule,
    MatFormFieldModule,
    DataGridComponent,
    MatProgressSpinnerModule,
  ],
})
export class CadModelDetailsDialogComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  public readonly dialogRef = inject(MatDialogRef);
  public readonly orderModelsService = inject(OrderModelsService);
  public readonly data = inject(MAT_DIALOG_DATA);
  private readonly locale = inject(LOCALE_ID);
  private readonly destroy = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly commentChanges$ = new Subject<CommentChangeEvent>();

  protected readonly DataDisplayType = DataDisplayType;

  protected readonly loading$ = new BehaviorSubject<boolean>(true);
  protected readonly materials$ = new BehaviorSubject<CadModelsDetailsInfoMaterial[]>([]);

  filterForm = this.formBuilder.group({
    materialName: [null as string | null],
    displayType: [DataDisplayType.Original as DataDisplayTypeValue],
  });

  ngOnInit(): void {
    this.commentChanges$
      .pipe(
        takeUntilDestroyed(this.destroy),
        map(event => {
          const materials = this.materials$.value;
          const detail = materials.flatMap(m => m.details).find(d => d.uid === event.uid);

          if (detail) {
            detail.comment = event.value;
            this.materials$.next([...materials]);
          }
        })
      )
      .subscribe();
  }

  protected readonly request$ = this.getMaterialsData().pipe(
    tap((materials: CadModelsDetailsInfoMaterial[]) => {
      this.materials$.next(materials);
      this.loading$.next(false);
    }),
    shareReplay(1)
  );

  protected readonly filtersChanged$ = this.filterForm.valueChanges.pipe(
    startWith(this.filterForm.value),
    shareReplay(1)
  );

  protected readonly state$ = combineLatest([this.request$, this.filtersChanged$]).pipe(
    map(([materials, filters]) => {
      const details = materials
        .filter(material => !filters.materialName || material.name === filters.materialName)
        .flatMap(material =>
          material.details.map(detail => ({
            ...detail,
            materialName: material.name,
          }))
        );

      return { materials, filters, details };
    }),
    shareReplay(1)
  );

  protected readonly displayedColumns$ = this.state$.pipe(
    map(({ details, filters }) => {
      const isOriginal = filters.displayType === DataDisplayType.Original;
      const showMaterialColumn = !filters.materialName;

      const columns: GridColDef<CadModelsDetailsInfoMaterial>[] = [
        {
          field: isOriginal ? 'originalPosition' : 'position',
          headerName: $localize`Позиция`,
          description: (params: GridDescriptionParams): string =>
            isOriginal
              ? $localize`Позиция в производстве: ${params.row.position}`
              : $localize`Позиция в оригинале: ${params.row.originalPosition}`,
          valueGetter: (params: GridValueGetterParams): string => {
            const isOriginal = this.filterForm.get('displayType').value === DataDisplayType.Original;
            return isOriginal ? params.row?.originalPosition : params.row?.position;
          },
        },
        { field: 'name', headerName: $localize`Наименование` },
      ];

      if (showMaterialColumn) {
        columns.push({
          field: 'materialName',
          headerName: $localize`Материал`,
          valueGetter: (params: GridValueGetterParams): string => {
            return params.row?.materialName || '';
          },
        });
      }

      columns.push(
        {
          field: 'finishLenght',
          headerName: $localize`Длина`,
          valueGetter: (params: GridValueGetterParams): string =>
            formatNumber(params.row?.finishLenght, this.locale, '1.0-2'),
        },
        {
          field: 'finishWidth',
          headerName: $localize`Ширина`,
          valueGetter: (params: GridValueGetterParams): string =>
            formatNumber(params.row?.finishWidth, this.locale, '1.0-2'),
        },
        { field: 'count', headerName: $localize`Количество` },
        { field: 'texture', headerName: $localize`Текстура` },
        { field: 'l1Sign', headerName: $localize`L1` },
        { field: 'l2Sign', headerName: $localize`L2` },
        { field: 'w1Sign', headerName: $localize`W1` },
        { field: 'w2Sign', headerName: $localize`W2` },
        { field: 'grove', headerName: $localize`Пазы` },
        {
          field: 'comment',
          headerName: $localize`Примечание`,
          renderCell: (): GridRenderCellProps => {
            return {
              component: CadModelDetailsCellNoteComponent,
              data: {
                saveChanges: this.saveChanges.bind(this),
              },
            };
          },
        }
      );

      const hasDesignationData = details.some(item => (isOriginal ? !!item.originalDesignation : !!item.designation));

      if (hasDesignationData) {
        columns.splice(1, 0, {
          field: isOriginal ? 'originalDesignation' : 'designation',
          headerName: $localize`Обозначение`,
          description: (params: GridDescriptionParams): string =>
            isOriginal
              ? $localize`Обозначение в производстве: ${params.row?.designation || '-'}`
              : $localize`Обозначение в оригинале: ${params.row?.originalDesignation || '-'}`,
          valueGetter: (params: GridValueGetterParams): string => {
            const isOriginal = this.filterForm.get('displayType').value === DataDisplayType.Original;
            return isOriginal ? params.row?.originalDesignation : params.row?.designation;
          },
        });
      }

      return columns;
    })
  );

  protected readonly data$ = this.state$.pipe(map(({ details }) => details));

  private getMaterialsData(): Observable<CadModelsDetailsInfoMaterial[]> {
    return this.orderModelsService.getCadModelDetails(this.data.modelId);
  }

  protected saveChanges(event?: CommentChangeEvent): void {
    if (event) {
      this.commentChanges$.next(event);
      return;
    }

    if (this.loading$.value) return;

    this.loading$.next(true);

    this.state$
      .pipe(
        takeUntilDestroyed(this.destroy),
        map(({ materials }) => materials),
        switchMap(materials =>
          this.orderModelsService.updateCadModelDetails(this.data.modelId, materials).pipe(
            finalize(() => this.loading$.next(false)),
            tap(() => this.snackBar.open($localize`Примечания сохранены`))
          )
        )
      )
      .subscribe();
  }
}
